import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { FiUsers, FiEdit2, FiSave, FiTrash2, FiPlus, FiCamera, FiChevronDown, FiChevronUp, FiUserPlus, FiUserX, FiSettings, FiCheck, FiXCircle } from 'react-icons/fi';
import ModalCloseButton from '../Common/ModalCloseButton';
import api from '../../services/api';
import { REGISTRATION_DEPARTMENT_OPTIONS } from '../../constants/registrationDepartmentOptions';
import { useApp } from '../../context/AppContext';
import { canSeeManagementUserRights } from '../../utils/adminRoles';
import { toCyrillicFio } from '../../utils/latinToCyrillicFio';
import { builtEmployeeWindowsName, findLocalWindowsUserForEmployee } from '../../utils/windowsAccountMatch';

// Пароль для Windows: минимум 8 символов, верхний/нижний регистр, цифра, спецсимвол; не должен содержать имя пользователя
function generateWindowsCompliantPassword(length = 12) {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  const digits = '23456789';
  const special = '!@#$%^&*';
  const all = upper + lower + digits + special;
  const pick = (str) => str.charAt(Math.floor(Math.random() * str.length));
  let pwd = pick(upper) + pick(lower) + pick(digits) + pick(special);
  for (let i = pwd.length; i < length; i++) pwd += pick(all);
  return pwd.split('').sort(() => Math.random() - 0.5).join('');
}

function validateWindowsPassword(password, username = '') {
  if (!password || password.length < 8) return 'Пароль должен быть не менее 8 символов.';
  if (!/[A-ZА-ЯЁ]/.test(password)) return 'Добавьте хотя бы одну заглавную букву.';
  if (!/[a-zа-яё]/.test(password)) return 'Добавьте хотя бы одну строчную букву.';
  if (!/\d/.test(password)) return 'Добавьте хотя бы одну цифру.';
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) return 'Добавьте хотя бы один спецсимвол (!@#$%^&*).';
  if (username && password.toLowerCase().includes(username.toLowerCase())) return 'Пароль не должен содержать имя пользователя.';
  return null;
}

function normalizePersonNameKey(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Ключи для сопоставления строки company_structure с username/fio из users (личный кабинет). */
function employeeNameKeysForAvatarMatch(emp) {
  const f = String(emp?.first_name || '').trim();
  const l = String(emp?.last_name || '').trim();
  if (!f && !l) return [];
  if (!l || l === '-') return [normalizePersonNameKey(f)].filter(Boolean);
  return [normalizePersonNameKey(`${f} ${l}`), normalizePersonNameKey(`${l} ${f}`)].filter(Boolean);
}

/** Учётка мессенджера для строки company_structure: user_id → users, затем ФИО как username/fio. */
function findMessengerUserForCompanyEmployee(emp, appUsersList) {
  const list = appUsersList || [];
  const uid = emp.user_id != null && emp.user_id !== '' ? Number(emp.user_id) : NaN;
  if (!Number.isNaN(uid) && uid > 0) {
    const byLink = list.find((u) => Number(u.id) === uid);
    if (byLink) return byLink;
  }
  const keys = employeeNameKeysForAvatarMatch(emp);
  if (!keys.length) return null;
  return (
    list.find((u) => {
      const un = normalizePersonNameKey(u.username);
      const fio = normalizePersonNameKey(u.fio);
      for (const k of keys) {
        if (k && (k === un || k === fio)) return true;
      }
      return false;
    }) || null
  );
}

const ModalOverlay = styled.div`
  position: fixed !important;
  top: 0 !important;
  left: 320px !important;
  right: 0 !important;
  bottom: 0 !important;
  width: calc(100vw - 320px) !important;
  height: 100vh !important;
  background: rgba(0, 0, 0, 0.45) !important;
  display: flex !important;
  align-items: stretch !important;
  justify-content: flex-start !important;
  z-index: 1000001 !important;
  padding: 0 !important;
  margin: 0 !important;
  box-sizing: border-box !important;
  transform: none !important;

  @media (max-width: 768px) {
    left: 0 !important;
    width: 100vw !important;
    align-items: center;
    justify-content: center;
  }
`;

const ModalContainer = styled.div`
  background: linear-gradient(135deg, #232931 0%, #181c22 100%);
  border-radius: 0 !important;
  width: 100% !important;
  height: 100% !important;
  max-width: 100% !important;
  max-height: 100% !important;
  min-width: 100% !important;
  min-height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  box-shadow: none !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
  position: relative !important;
  color: #fff;

  @media (max-width: 768px) {
    border-radius: 20px;
    width: 90%;
    height: 90vh;
    max-width: 1170px;
    max-height: 900px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => (props.$compact ? '12px 14px' : '20px 32px')};
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  flex-shrink: 0;
  z-index: 1000;
`;

const ModalTitle = styled.h2`
  margin: 0;
  flex: 1;
  min-width: 0;
  color: #ffffff;
  font-size: ${props => (props.$compact ? '1.1rem' : '1.8rem')};
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: ${props => (props.$compact ? '8px' : '12px')};
`;

const ModalBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  background: #f9fafb;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => (props.$compact ? '8px' : '12px')};
  padding: ${props => (props.$compact ? '10px 12px' : '16px 24px')};
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
  flex-wrap: wrap;
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: ${props => (props.$compact ? '0' : '200px')};
  padding: ${props => (props.$compact ? '8px 12px' : '10px 16px')};
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  font-size: ${props => (props.$compact ? '0.85rem' : '0.95rem')};
  outline: none;
  transition: all 0.3s ease;
  
  &:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const TableContainer = styled.div`
  padding: ${props => (props.$compact ? '10px 12px' : '24px')};
  padding-top: ${props => (props.$compact ? '10px' : '16px')};
  background: #f9fafb;
`;

/** Область прокрутки таблицы: горизонтальный скролл всегда у нижнего края видимой зоны, не в конце списка */
const TableScrollViewport = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-width: 1200px;
`;

const TableHeader = styled.thead`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  color: #ffffff;
  font-weight: 700;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f9fafb;
  }
  
  ${props => props.$editing && `
    background-color: #fef3c7;
  `}
`;

const TableCell = styled.td`
  padding: 12px 16px;
  color: #111827;
  font-size: 0.9rem;
`;

const EditableInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 2px solid #667eea;
  border-radius: 6px;
  font-size: 0.9rem;
  outline: none;
  background: #ffffff;
  
  &:focus {
    border-color: #764ba2;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const EditableSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 2px solid #667eea;
  border-radius: 6px;
  font-size: 0.9rem;
  outline: none;
  background: #ffffff;
  cursor: pointer;
  
  &:focus {
    border-color: #764ba2;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const DateInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 2px solid #667eea;
  border-radius: 6px;
  font-size: 0.9rem;
  outline: none;
  background: #ffffff;
  
  &:focus {
    border-color: #764ba2;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ActionButton = styled.button`
  padding: ${props => (props.$compact ? '6px 10px' : '8px 16px')};
  border: none;
  border-radius: 8px;
  font-size: ${props => (props.$compact ? '0.72rem' : '0.85rem')};
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${props => (props.$compact ? '4px' : '6px')};
  transition: all 0.2s;
  margin-right: ${props => (props.$compact ? '0' : '8px')};
  line-height: ${props => (props.$compact ? '1.25' : 'normal')};
  
  ${props => props.$type === 'edit' && `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #ffffff;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
    }
  `}
  
  ${props => props.$type === 'save' && `
    background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
    color: #232931;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(67, 233, 123, 0.3);
    }
  `}
  
  ${props => props.$type === 'cancel' && `
    background: #e5e7eb;
    color: #6b7280;
    
    &:hover {
      background: #d1d5db;
    }
  `}
  
  ${props => props.$type === 'delete' && `
    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
    color: #ffffff;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(245, 87, 108, 0.3);
    }
  `}

  ${props => props.$type === 'win-enabled' && `
    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
    color: #ffffff;
    border: 1px solid rgba(21, 128, 61, 0.45);
    box-shadow: 0 2px 8px rgba(34, 197, 94, 0.25);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 14px rgba(34, 197, 94, 0.4);
      filter: brightness(1.05);
    }
  `}

  ${props => props.$type === 'win-disabled' && `
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: #ffffff;
    border: 1px solid rgba(185, 28, 28, 0.45);
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.25);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 14px rgba(239, 68, 68, 0.4);
      filter: brightness(1.05);
    }
  `}
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #6b7280;
  font-size: ${props => (props.$compact ? '0.95rem' : '1.2rem')};
  font-weight: 500;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #6b7280;
  font-size: ${props => (props.$compact ? '0.95rem' : '1.2rem')};
  font-weight: 500;
`;

const TabsRow = styled.div`
  display: flex;
  gap: ${props => (props.$compact ? '6px' : '4px')};
  padding: ${props => (props.$compact ? '8px 10px' : '12px 24px')};
  background: #1e293b;
  border-bottom: 1px solid rgba(148,163,184,0.2);
  flex-shrink: 0;
  flex-wrap: ${props => (props.$compact ? 'wrap' : 'nowrap')};
`;

const TabButton = styled.button`
  padding: ${props => (props.$compact ? '7px 10px' : '10px 20px')};
  border: none;
  border-radius: 10px;
  font-size: ${props => (props.$compact ? '0.72rem' : '0.95rem')};
  font-weight: 600;
  cursor: pointer;
  background: ${props => props.$active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'rgba(148,163,184,0.15)'};
  color: ${props => props.$active ? '#fff' : '#94a3b8'};
  transition: all 0.2s;
  &:hover {
    background: ${props => props.$active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'rgba(148,163,184,0.25)'};
    color: #e2e8f0;
  }
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TabPanel = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const MobileEmployeeCard = styled.div`
  background: #ffffff;
  border-radius: 10px;
  padding: ${props => (props.$dense ? '8px 10px' : '10px 12px')};
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  color: #111827;

  ${props => props.$editing && `
    background-color: #fef3c7;
  `}
`;

const MobileCardTop = styled.div`
  display: flex;
  gap: ${props => (props.$tight ? '8px' : '10px')};
  align-items: ${props => (props.$center ? 'center' : 'flex-start')};
`;

const MobileFieldLabel = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 4px;
  font-weight: 600;
`;

const MobileFieldBlock = styled.div`
  margin-bottom: 8px;
`;

const MobileActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: ${props => (props.$tight ? '6px' : '10px')};
`;

const MobileAvatarButton = styled.button`
  border: none;
  padding: 0;
  background: none;
  cursor: pointer;
  width: ${props => (props.$small ? '38px' : '44px')};
  height: ${props => (props.$small ? '38px' : '44px')};
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
`;

const MobileAvatarImg = styled.img`
  width: ${props => (props.$small ? '38px' : '44px')};
  height: ${props => (props.$small ? '38px' : '44px')};
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e5e7eb;
`;

const MobileAvatarPh = styled.div`
  width: ${props => (props.$small ? '38px' : '44px')};
  height: ${props => (props.$small ? '38px' : '44px')};
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: ${props => (props.$small ? '12px' : '14px')};
  font-weight: 700;
  border: 2px solid #e5e7eb;
`;

const MobileCardsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => (props.$tight ? '7px' : '10px')};
`;

const MobileEmployeeDetailsExpand = styled.div`
  margin-top: 8px;
  padding-top: 10px;
  border-top: 1px solid #e5e7eb;
`;

const TabSection = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: ${props => (props.$compact ? '10px 12px' : '24px')};
  box-sizing: border-box;
`;

const TabIntro = styled.p`
  margin: ${props => (props.$compact ? '0 0 10px 0' : '0 0 16px 0')};
  font-size: ${props => (props.$compact ? '0.78rem' : '14px')};
  color: #94a3b8;
  line-height: ${props => (props.$compact ? '1.4' : 'normal')};
  flex-shrink: 0;
`;

const MobileCompactInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  font-size: 0.85rem;
  background: #ffffff;
  color: #111827;
  outline: none;

  &:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.15);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const AvatarCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  cursor: pointer;
  position: relative;
`;

const AvatarImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e5e7eb;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #667eea;
    transform: scale(1.05);
  }
`;

const AvatarPlaceholder = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 20px;
  font-weight: 700;
  border: 2px solid #e5e7eb;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    border-color: #667eea;
    transform: scale(1.05);
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const copyablePasswordValueStyle = {
  userSelect: 'text',
  WebkitUserSelect: 'text',
  msUserSelect: 'text',
  MozUserSelect: 'text',
  cursor: 'text',
  pointerEvents: 'auto'
};

const AvatarUploadWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const AddButton = styled.button`
  padding: ${props => (props.$compact ? '8px 12px' : '10px 20px')};
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
  color: #232931;
  cursor: pointer;
  font-size: ${props => (props.$compact ? '0.8rem' : '0.95rem')};
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 8px rgba(67, 233, 123, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(67, 233, 123, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const AddEmployeeForm = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: ${props => (props.$compact ? '14px' : '24px')};
  margin-bottom: ${props => (props.$compact ? '12px' : '24px')};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 2px solid #667eea;
`;

const FormTitle = styled.h3`
  margin: ${props => (props.$compact ? '0 0 12px 0' : '0 0 20px 0')};
  color: #111827;
  font-size: ${props => (props.$compact ? '1.05rem' : '1.3rem')};
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 20px;
  
  @media (max-width: 1400px) {
    grid-template-columns: 1fr;
  }
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormLabel = styled.label`
  color: #374151;
  font-size: 0.9rem;
  font-weight: 600;
`;

const FormInput = styled.input`
  padding: 10px 14px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.95rem;
  outline: none;
  transition: all 0.3s ease;
  background: #ffffff;
  color: #111827;
  
  &:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const FormSelect = styled.select`
  padding: 10px 14px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.95rem;
  outline: none;
  transition: all 0.3s ease;
  background: #ffffff;
  color: #111827;
  cursor: pointer;
  
  &:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

function SuggestInput({ as: Comp = FormInput, value, onChange, options, listId, placeholder }) {
  return (
    <>
      <Comp
        type="text"
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        list={listId}
        autoComplete="off"
      />
      <datalist id={listId}>
        {(options || []).map((opt) => (
          <option key={opt} value={opt} />
        ))}
      </datalist>
    </>
  );
}

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  border: 2px solid #d1d5db;
  background: #ffffff;
  color: #6b7280;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
`;

const SubmitButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

/** Событие на document: открыть модалку прав (когда список сотрудников открыт из ChatArea без колбэка). */
export const OPEN_USER_RIGHTS_MODAL_EVENT = 'openUserRightsModalRequest';

export default function CompanyEmployeesListModal({ open, onClose, isMobile = false, onOpenUserRights }) {
  const { state } = useApp();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [departments, setDepartments] = useState([]);
  const [positionOptions, setPositionOptions] = useState([...REGISTRATION_DEPARTMENT_OPTIONS]);
  const [subdivisions, setSubdivisions] = useState([]);
  const [managers, setManagers] = useState([]);
const [appUsers, setAppUsers] = useState([]);
const [appUsersLoading, setAppUsersLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('company');
  /** Вкладка «В мессенджере»: все / только с учёткой / только без */
  const [messengerRegistryFilter, setMessengerRegistryFilter] = useState('all');
  /** Диалог создания учётки мессенджера (поля как при регистрации) */
  const [createMessengerEmployee, setCreateMessengerEmployee] = useState(null);
  const [createMessengerForm, setCreateMessengerForm] = useState({
    password: '',
    birth_day: '',
    birth_month: '',
    birth_year: '',
    department: '',
  });
  const [createMessengerSaving, setCreateMessengerSaving] = useState(false);
  const [localWindowsUsers, setLocalWindowsUsers] = useState([]);
  const [localWindowsUsersLoading, setLocalWindowsUsersLoading] = useState(false);
  const [expandedActionRows, setExpandedActionRows] = useState(new Set());
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    first_name: '',
    last_name: '',
    position: '',
    department: '',
    subdivision: '',
    location: '',
    employment_type: '',
    start_date: '',
    end_date: '',
    termination_date: '',
    manager_name: '',
    password: '',
    avatar_file: null,
    avatar_preview: null
  });
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(null);
  const fileInputRef = useRef({});
  const overlayRef = useRef(null);
  const containerRef = useRef(null);

  // Применяем стили для полного экрана через DOM manipulation (на мобильном — на весь экран, поверх других модалок)
  useEffect(() => {
    if (!open) return;
    
    const applyStyles = () => {
      // Ищем элемент по ID или через ref
      const overlay = document.getElementById('company-employees-list-modal-overlay') || overlayRef.current;
      if (overlay) {
        if (isMobile) {
          overlay.style.setProperty('left', '0', 'important');
          overlay.style.setProperty('width', '100vw', 'important');
          overlay.style.setProperty('z-index', '1000080', 'important');
        } else {
          overlay.style.setProperty('left', '320px', 'important');
          overlay.style.setProperty('width', 'calc(100vw - 320px)', 'important');
          overlay.style.setProperty('z-index', '1000001', 'important');
        }
        overlay.style.setProperty('right', '0', 'important');
        overlay.style.setProperty('top', '0', 'important');
        overlay.style.setProperty('bottom', '0', 'important');
        overlay.style.setProperty('height', '100vh', 'important');
        overlay.style.setProperty('margin', '0', 'important');
        overlay.style.setProperty('padding', '0', 'important');
        overlay.style.setProperty('position', 'fixed', 'important');
        overlay.style.setProperty('display', 'flex', 'important');
        overlay.style.setProperty('align-items', 'stretch', 'important');
        overlay.style.setProperty('justify-content', 'flex-start', 'important');
        overlay.style.setProperty('background', 'rgba(0,0,0,0.45)', 'important');
        overlay.style.setProperty('transform', 'none', 'important');
      }
      
      const container = containerRef.current || (overlay?.firstElementChild);
      if (container) {
        container.style.setProperty('border-radius', '0', 'important');
        container.style.setProperty('box-shadow', 'none', 'important');
        container.style.setProperty('margin', '0', 'important');
        container.style.setProperty('padding', '0', 'important');
        container.style.setProperty('width', '100%', 'important');
        container.style.setProperty('height', '100%', 'important');
        container.style.setProperty('max-width', '100%', 'important');
        container.style.setProperty('max-height', '100%', 'important');
        container.style.setProperty('min-width', '100%', 'important');
        container.style.setProperty('min-height', '100%', 'important');
      }
    };
    
    // Применяем стили сразу и с задержками
    applyStyles();
    const timeoutId1 = setTimeout(applyStyles, 10);
    const timeoutId2 = setTimeout(applyStyles, 50);
    const timeoutId3 = setTimeout(applyStyles, 100);
    const timeoutId4 = setTimeout(applyStyles, 200);
    const timeoutId5 = setTimeout(applyStyles, 500);
    const timeoutId6 = setTimeout(applyStyles, 1000);
    
    // Дополнительная проверка через requestAnimationFrame для гарантии
    const rafId1 = requestAnimationFrame(() => {
      applyStyles();
      requestAnimationFrame(() => {
        applyStyles();
      });
    });
    
    // MutationObserver для отслеживания изменений в DOM
    const observer = new MutationObserver(() => {
      applyStyles();
    });
    
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
    
    // Добавляем глобальные стили в начало head для максимального приоритета
    const styleId = 'company-employees-list-modal-overlay-styles';
    let existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }
    
    const style = document.createElement('style');
    style.id = styleId;
    const overlayPos = isMobile
      ? `left: 0 !important;
        width: 100vw !important;
        z-index: 1000080 !important;`
      : `left: 320px !important;
        width: calc(100vw - 320px) !important;
        z-index: 1000001 !important;`;
    style.textContent = `
      body > #company-employees-list-modal-overlay,
      body > div[data-overlay-id="company-employees-list"],
      body > div[class*="ModalOverlay"][id*="company-employees"],
      #company-employees-list-modal-overlay,
      div[data-overlay-id="company-employees-list"],
      div[class*="ModalOverlay"][id*="company-employees"] {
        ${overlayPos}
        right: 0 !important;
        background: rgba(0,0,0,0.45) !important;
        margin: 0 !important;
        padding: 0 !important;
        top: 0 !important;
        bottom: 0 !important;
        height: 100vh !important;
        position: fixed !important;
        display: flex !important;
        align-items: stretch !important;
        justify-content: flex-start !important;
        transform: none !important;
        overflow: visible !important;
        box-sizing: border-box !important;
      }
      body > #company-employees-list-modal-overlay > div,
      body > div[data-overlay-id="company-employees-list"] > div,
      body > div[class*="ModalOverlay"][id*="company-employees"] > div[class*="ModalContainer"],
      #company-employees-list-modal-overlay > div,
      div[data-overlay-id="company-employees-list"] > div,
      div[class*="ModalOverlay"][id*="company-employees"] > div[class*="ModalContainer"] {
        border-radius: 0 !important;
        box-shadow: none !important;
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
        height: 100% !important;
        max-width: 100% !important;
        max-height: 100% !important;
        min-width: 100% !important;
        min-height: 100% !important;
        pointer-events: auto !important;
        overflow: hidden !important;
        box-sizing: border-box !important;
        position: relative !important;
      }
      #company-employees-modal-body {
        min-height: 0 !important;
        overflow: hidden !important;
        display: flex !important;
        flex-direction: column !important;
      }
      #company-employees-modal-body > div {
        flex: 1 !important;
        min-height: 0 !important;
        overflow: hidden !important;
        display: flex !important;
        flex-direction: column !important;
      }
      #company-employees-list-modal-overlay *,
      div[data-overlay-id="company-employees-list"] * {
        pointer-events: auto !important;
      }
    `;
    // Вставляем в начало head для максимального приоритета
    if (document.head.firstChild) {
      document.head.insertBefore(style, document.head.firstChild);
    } else {
      document.head.appendChild(style);
    }
    
    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId1);
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
      clearTimeout(timeoutId4);
      clearTimeout(timeoutId5);
      clearTimeout(timeoutId6);
      const styleToRemove = document.getElementById(styleId);
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, [open, isMobile]);

  useEffect(() => {
    if (open) {
      loadEmployees();
      loadAppUsers();
      loadLocalWindowsUsers();
    }
  }, [open]);

  useEffect(() => {
    if (open && activeTab === 'windows-local') {
      loadLocalWindowsUsers();
    }
  }, [open, activeTab]);

  const loadLocalWindowsUsers = async () => {
    setLocalWindowsUsersLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/local-users', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        if (response.status === 403) {
          setLocalWindowsUsers([]);
          return;
        }
        throw new Error('Ошибка загрузки локальных пользователей Windows');
      }
      const data = await response.json();
      setLocalWindowsUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Ошибка загрузки локальных пользователей Windows:', error);
      setLocalWindowsUsers([]);
    } finally {
      setLocalWindowsUsersLoading(false);
    }
  };

  const loadEmployees = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Используем endpoint для получения плоского списка
      const response = await fetch('/api/company-structure/list', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Ошибка загрузки сотрудников');
      }
      
      const data = await response.json();
      setEmployees(Array.isArray(data) ? data : []);
      
      // Собираем уникальные значения для фильтров
      const depts = new Set();
      const posts = new Set(REGISTRATION_DEPARTMENT_OPTIONS);
      const subs = new Set();
      const mgrs = new Set();
      
      (Array.isArray(data) ? data : []).forEach(emp => {
        if (emp.department) depts.add(emp.department);
        if (emp.position) posts.add(emp.position);
        if (emp.subdivision) subs.add(emp.subdivision);
        if (emp.manager_name) mgrs.add(emp.manager_name);
      });
      
      const localeSort = (a, b) => String(a).localeCompare(String(b), 'ru');
      setDepartments(Array.from(depts).sort(localeSort));
      setPositionOptions(Array.from(posts).sort(localeSort));
      setSubdivisions(Array.from(subs).sort());
      setManagers(Array.from(mgrs).sort());
    } catch (error) {
      console.error('Ошибка загрузки сотрудников:', error);
      alert('Ошибка загрузки данных: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAppUsers = async () => {
    setAppUsersLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/users', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Ошибка загрузки пользователей приложения');
      }

      const data = await response.json();
      const list = Array.isArray(data) ? data : (Array.isArray(data.users) ? data.users : []);
      setAppUsers(list);
    } catch (error) {
      console.error('Ошибка загрузки пользователей приложения:', error);
    } finally {
      setAppUsersLoading(false);
    }
  };

  const handleEdit = (employee) => {
    setExpandedActionRows((prev) => {
      const next = new Set(prev);
      next.delete(employee.id);
      return next;
    });
    setEditingId(employee.id);
    setEditingData({
      first_name: employee.first_name || '',
      last_name: employee.last_name || '',
      position: employee.position || '',
      department: employee.department || '',
      subdivision: employee.subdivision || '',
      location: employee.location || '',
      employment_type: employee.employment_type || '',
      start_date: employee.start_date ? employee.start_date.split('T')[0] : '',
      end_date: employee.end_date ? employee.end_date.split('T')[0] : '',
      termination_date: employee.termination_date ? employee.termination_date.split('T')[0] : '',
      manager_name: employee.manager_name || '',
      password: employee.password || '' // Добавляем пароль
    });
  };

  const handleSave = async (employeeId) => {
    try {
      const token = localStorage.getItem('token');
      const { password, ...dataToSave } = editingData;
      const response = await fetch(`/api/company-structure/${employeeId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSave)
      });
      
      if (!response.ok) {
        throw new Error('Ошибка сохранения данных');
      }
      
      // Обновляем локальное состояние
      setEmployees(employees.map(emp => 
        emp.id === employeeId 
          ? { ...emp, ...editingData }
          : emp
      ));
      
      setEditingId(null);
      setEditingData({});
      
      // Отправляем событие для обновления структуры компании
      window.dispatchEvent(new CustomEvent('companyStructureUpdated'));
      
      alert('Данные успешно сохранены');
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      alert('Ошибка сохранения: ' + error.message);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditingData({});
  };

  const handleAddEmployee = async () => {
    if (!newEmployee.first_name || !newEmployee.last_name) {
      alert('Пожалуйста, заполните имя и фамилию сотрудника');
      return;
    }

    if (!newEmployee.password || newEmployee.password.length < 8) {
      alert('Пожалуйста, введите пароль (минимум 8 символов для Windows)');
      return;
    }

    const winUsername = `${newEmployee.first_name || ''} ${newEmployee.last_name || ''}`.trim();
    const windowsPasswordError = validateWindowsPassword(String(newEmployee.password || ''), winUsername);
    if (windowsPasswordError) {
      alert(`Пароль не подходит для Windows: ${windowsPasswordError}`);
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Если есть файл аватарки, загружаем его отдельно
      let avatarUrl = null;
      if (newEmployee.avatar_file) {
        const formData = new FormData();
        formData.append('avatar', newEmployee.avatar_file);
        
        const avatarResponse = await fetch('/api/company-structure/upload-avatar', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        if (!avatarResponse.ok) {
          throw new Error('Ошибка загрузки аватарки');
        }
        
        const avatarData = await avatarResponse.json();
        avatarUrl = avatarData.avatar_url;
      }
      
      const response = await fetch('/api/company-structure', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newEmployee,
          avatar_url: avatarUrl,
          avatar_file: undefined,
          avatar_preview: undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = [errorData.error, errorData.details].filter(Boolean).join(': ') || 'Ошибка добавления сотрудника';
        throw new Error(errorMessage);
      }

      // Очищаем форму
      setNewEmployee({
        first_name: '',
        last_name: '',
        position: '',
        department: '',
        subdivision: '',
        location: '',
        employment_type: '',
        start_date: '',
        end_date: '',
        termination_date: '',
        manager_name: '',
        password: '',
        avatar_file: null,
        avatar_preview: null
      });
      setShowAddForm(false);

      // Перезагружаем список
      await loadEmployees();
      
      // Отправляем событие для обновления структуры компании
      window.dispatchEvent(new CustomEvent('companyStructureUpdated'));
      
      alert('Сотрудник успешно добавлен');
    } catch (error) {
      console.error('Ошибка добавления сотрудника:', error);
      const errText = `Ошибка добавления сотрудника.\n\nСкопируйте текст ниже и отправьте администратору:\n\n${error?.message || 'Неизвестная ошибка'}`;
      // prompt позволяет выделить и скопировать весь текст ошибки
      window.prompt('Ошибка добавления (Ctrl+C, Enter):', errText);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (employeeId, employeeName) => {
    const fullName = `${employeeName.first_name || ''} ${employeeName.last_name || ''}`.trim();
    const confirmMessage = `Вы уверены, что хотите полностью удалить сотрудника "${fullName}"?\n\nБудет удалено:\n• запись из структуры компании;\n• учётная запись Windows (локальный пользователь);\n• учётная запись мессенджера.\n\nДействие нельзя отменить.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/company-structure/${employeeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ошибка удаления сотрудника');
      }

      // Удаляем сотрудника из локального состояния
      setEmployees(employees.filter(emp => emp.id !== employeeId));

      // Если редактируем удаляемого сотрудника, отменяем редактирование
      if (editingId === employeeId) {
        setEditingId(null);
        setEditingData({});
      }

      // Отправляем событие для обновления структуры компании
      window.dispatchEvent(new CustomEvent('companyStructureUpdated'));

      alert('Сотрудник, учётная запись Windows и учётка мессенджера удалены.');
    } catch (error) {
      console.error('Ошибка удаления сотрудника:', error);
      alert('Ошибка удаления: ' + error.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('uk-UA', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const normalizeAvatarUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    let trimmed = url.trim();
    trimmed = trimmed.replace(/\\/g, '/');
    
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    
    const idx = trimmed.toLowerCase().indexOf('/uploads/avatars/');
    if (idx !== -1) {
      return trimmed.slice(idx);
    }
    
    if (!trimmed) return null;
    if (trimmed.startsWith('/uploads') || trimmed.startsWith('/images')) {
      return trimmed;
    }
    if (trimmed.startsWith('uploads/')) {
      return '/' + trimmed;
    }
    return '/uploads/avatars/' + trimmed.replace(/^\/+/, '');
  };

  const getInitials = (firstName, lastName) => {
    const first = (firstName || '').charAt(0).toUpperCase();
    const last = (lastName || '').charAt(0).toUpperCase();
    return (first + last) || '?';
  };

  const handleAvatarClick = (employeeId) => {
    if (!fileInputRef.current[employeeId]) {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = (e) => handleAvatarUpload(employeeId, e.target.files[0]);
      fileInputRef.current[employeeId] = input;
    }
    fileInputRef.current[employeeId].click();
  };

  const handleAvatarUpload = async (employeeId, file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Пожалуйста, выберите изображение');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('Размер файла не должен превышать 10MB');
      return;
    }

    setUploadingAvatar(employeeId);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await fetch(`/api/company-structure/${employeeId}/avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ошибка загрузки аватарки');
      }

      const data = await response.json();

      setEmployees(employees.map(emp => 
        emp.id === employeeId 
          ? { ...emp, avatar_url: data.avatar_url }
          : emp
      ));

      window.dispatchEvent(new CustomEvent('companyStructureUpdated'));

      // Если это текущий пользователь, обновляем его состояние
      if (data.synced_user_id) {
        // Получаем текущего пользователя из localStorage
        const currentUserStr = localStorage.getItem('user');
        if (currentUserStr) {
          try {
            const currentUser = JSON.parse(currentUserStr);
            // Проверяем, что это текущий пользователь
            const employee = employees.find(emp => emp.id === employeeId);
            if (employee && currentUser.id && currentUser.id === data.synced_user_id) {
              // Обновляем состояние пользователя через событие (чтобы не нарушать правила хуков)
              window.dispatchEvent(new CustomEvent('updateUserAvatar', { detail: data.avatar_url }));
              console.log('✅ [Avatar Upload] Аватарка обновлена для текущего пользователя');
            }
          } catch (e) {
            console.error('Ошибка парсинга пользователя:', e);
          }
        }
      }

      alert('Аватарка успешно загружена');
    } catch (error) {
      console.error('Ошибка загрузки аватарки:', error);
      alert('Ошибка загрузки: ' + error.message);
    } finally {
      setUploadingAvatar(null);
    }
  };

  const filteredEmployees = employees.filter(emp => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const fullName = `${emp.first_name || ''} ${emp.last_name || ''}`.toLowerCase();
    const position = (emp.position || '').toLowerCase();
    const department = (emp.department || '').toLowerCase();
    const subdivision = (emp.subdivision || '').toLowerCase();
    
    return fullName.includes(query) || 
           position.includes(query) || 
           department.includes(query) || 
           subdivision.includes(query);
  });

  const employeeNamesSet = useMemo(() => {
    const set = new Set();
    (employees || []).forEach(emp => {
      const name = `${(emp.first_name || '').trim()} ${(emp.last_name || '').trim()}`.trim();
      if (name) set.add(name.toLowerCase());
    });
    return set;
  }, [employees]);

  const messengerAvatarFromUsersByName = useMemo(() => {
    const m = new Map();
    for (const u of appUsers || []) {
      const av = String(u.avatar || '').trim();
      if (!av) continue;
      for (const src of [u.username, u.fio]) {
        if (!src) continue;
        const k = normalizePersonNameKey(src);
        if (k && !m.has(k)) m.set(k, av);
      }
    }
    return m;
  }, [appUsers]);

  const getEmployeeListAvatarUrl = useCallback(
    (employee) => {
      const own = String(employee?.avatar_url || '').trim();
      if (own) return own;
      const fromEmployeesTable = String(employee?.messenger_avatar || '').trim();
      if (fromEmployeesTable) return fromEmployeesTable;
      for (const k of employeeNameKeysForAvatarMatch(employee)) {
        const a = messengerAvatarFromUsersByName.get(k);
        if (a) return a;
      }
      return null;
    },
    [messengerAvatarFromUsersByName]
  );

  const appUsersInStructure = useMemo(() => {
    return (appUsers || []).filter(u => {
      const username = (u.username || '').trim().toLowerCase();
      return username && employeeNamesSet.has(username);
    });
  }, [appUsers, employeeNamesSet]);

  const messengerUserForEmployeeMap = useMemo(() => {
    const map = new Map();
    for (const emp of employees || []) {
      map.set(emp.id, findMessengerUserForCompanyEmployee(emp, appUsers));
    }
    return map;
  }, [employees, appUsers]);

  const getWindowsUserForEmployee = useCallback((employee) => {
    const messengerUser = messengerUserForEmployeeMap.get(employee?.id);
    return findLocalWindowsUserForEmployee(employee, localWindowsUsers, messengerUser, toCyrillicFio);
  }, [localWindowsUsers, messengerUserForEmployeeMap]);

  const messengerLinkedCount = useMemo(() => {
    let n = 0;
    for (const emp of employees || []) {
      if (messengerUserForEmployeeMap.get(emp.id)) n += 1;
    }
    return n;
  }, [employees, messengerUserForEmployeeMap]);

  const messengerRegistryRows = useMemo(() => {
    let rows = filteredEmployees;
    if (messengerRegistryFilter === 'linked') {
      rows = rows.filter((e) => messengerUserForEmployeeMap.get(e.id));
    } else if (messengerRegistryFilter === 'unlinked') {
      rows = rows.filter((e) => !messengerUserForEmployeeMap.get(e.id));
    }
    return rows;
  }, [filteredEmployees, messengerRegistryFilter, messengerUserForEmployeeMap]);

  const initialRegistrationDepartment = useCallback((emp) => {
    const d = String(emp?.department || '').trim();
    if (d && REGISTRATION_DEPARTMENT_OPTIONS.includes(d)) return d;
    return '';
  }, []);

  const openCreateMessengerDialog = useCallback(
    (emp) => {
      setCreateMessengerForm({
        password: '',
        birth_day: '',
        birth_month: '',
        birth_year: '',
        department: initialRegistrationDepartment(emp),
      });
      setCreateMessengerEmployee(emp);
    },
    [initialRegistrationDepartment]
  );

  const closeCreateMessengerDialog = useCallback(() => {
    if (createMessengerSaving) return;
    setCreateMessengerEmployee(null);
  }, [createMessengerSaving]);

  const submitCreateMessengerAccount = async () => {
    if (!createMessengerEmployee) return;
    const { password, birth_day, birth_month, birth_year, department } = createMessengerForm;
    if (!password || String(password).length < 6) {
      alert('Введите пароль (минимум 6 символов).');
      return;
    }
    const bd = Number(birth_day);
    const bm = Number(birth_month);
    const by = Number(birth_year);
    if (!bd || !bm || !by || bd < 1 || bd > 31 || bm < 1 || bm > 12 || by < 1900) {
      alert('Укажите корректную дату рождения.');
      return;
    }
    if (!String(department || '').trim()) {
      alert('Выберите должность из списка.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Нет токена авторизации');
      return;
    }
    setCreateMessengerSaving(true);
    try {
      const res = await fetch(`/api/company-structure/${createMessengerEmployee.id}/messenger-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          password: String(password).trim(),
          birth_day: bd,
          birth_month: bm,
          birth_year: by,
          department: String(department).trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || data.message || 'Ошибка создания учётки');
      }
      alert(data.message || 'Учётная запись мессенджера создана.');
      setCreateMessengerEmployee(null);
      await loadEmployees();
      await loadAppUsers();
    } catch (e) {
      console.error(e);
      alert(e.message || 'Ошибка');
    } finally {
      setCreateMessengerSaving(false);
    }
  };

  const deleteMessengerAccountForEmployee = async (emp) => {
    const has = messengerUserForEmployeeMap.get(emp.id);
    if (!has) {
      alert('У этого сотрудника нет учётной записи в мессенджере.');
      return;
    }
    const ok = window.confirm(
      'Удалить учётную запись для входа в мессенджер?\n\n' +
        'Локальная учётная запись Windows (RDP) не будет изменена.\n' +
        'Чаты и сообщения этого пользователя в приложении будут затронуты согласно правилам сервера.'
    );
    if (!ok) return;
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Нет токена авторизации');
      return;
    }
    try {
      const res = await fetch(`/api/company-structure/${emp.id}/messenger-account`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || data.message || 'Ошибка удаления');
      }
      alert(data.message || 'Учётная запись мессенджера удалена.');
      await loadEmployees();
      await loadAppUsers();
    } catch (e) {
      console.error(e);
      alert(e.message || 'Ошибка');
    }
  };

  const handleResetPasswordWindowsLocal = async (winUser) => {
    const name = (winUser.Name || winUser.name || '').trim();
    if (!name) return;
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Нет токена авторизации');
      return;
    }
    const useCustom = window.confirm('Нажмите OK, чтобы ввести свой пароль.\nНажмите Отмена, чтобы сгенерировать пароль, соответствующий политике Windows.');
    let newPassword = '';
    if (useCustom) {
      newPassword = window.prompt(
        `Введите новый пароль для "${name}" (для Windows: мин. 8 символов, заглавные и строчные буквы, цифры, спецсимволы !@#$%^&*; не должен содержать имя пользователя и не совпадать с ранее использованными):`,
        ''
      );
      if (!newPassword) return;
      newPassword = String(newPassword).trim();
      const err = validateWindowsPassword(newPassword, name);
      if (err) {
        alert(err);
        return;
      }
    } else {
      newPassword = generateWindowsCompliantPassword(12);
    }
    try {
      const winRes = await fetch(`/api/admin/local-users/${encodeURIComponent(name)}/password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ password: newPassword })
      });
      const winData = await winRes.json().catch(() => ({}));
      if (!winRes.ok || winData.success === false) {
        throw new Error(winData.error || winData.message || 'Ошибка установки пароля в Windows');
      }
      const syncRes = await fetch('/api/admin/sync-messenger-user-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ username: name, password: newPassword })
      });
      const syncData = await syncRes.json().catch(() => ({}));
      if (!syncRes.ok || syncData.success === false) {
        console.warn('Пароль в Windows установлен, но синхронизация с мессенджером не удалась:', syncData.error);
        alert('Пароль установлен в Windows. Синхронизация с мессенджером не удалась: ' + (syncData.error || 'неизвестная ошибка') + '. Обратитесь к администратору или повторите сброс пароля.');
      }
      if (!useCustom) {
        window.prompt('Пароль установлен в Windows и в мессенджере. Передайте сотруднику новый пароль:', newPassword);
      } else {
        alert('Пароль установлен для учётки Windows и для входа в мессенджер.');
      }
      loadLocalWindowsUsers();
      loadEmployees();
    } catch (err) {
      console.error('Ошибка сброса пароля (Windows local):', err);
      alert('Ошибка: ' + err.message);
    }
  };

  // Сброс пароля только для входа в мессенджер (по строке из вкладки «Сотрудники компании»)
  const handleResetPasswordMessenger = async (employee) => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Нет токена авторизации');
      return;
    }
    const useCustom = window.confirm('Сбросить пароль для входа в мессенджер.\nOK — ввести свой пароль.\nОтмена — сгенерировать случайный.');
    let newPassword = '';
    if (useCustom) {
      newPassword = window.prompt(`Введите новый пароль для входа в мессенджер (мин. 6 символов):`, '');
      if (!newPassword) return;
      newPassword = String(newPassword).trim();
      if (newPassword.length < 6) {
        alert('Пароль должен содержать минимум 6 символов');
        return;
      }
    } else {
      newPassword = generateWindowsCompliantPassword(12);
    }
    try {
      const res = await fetch(`/api/company-structure/${employee.id}/reset-messenger-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ newPassword })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) {
        throw new Error(data.error || data.message || 'Ошибка сброса пароля');
      }
      if (!useCustom) {
        window.prompt('Пароль для входа в мессенджер сброшен. Передайте сотруднику новый пароль:', newPassword);
      } else {
        alert('Пароль для входа в мессенджер обновлён.');
      }
      loadEmployees();
    } catch (err) {
      console.error('Ошибка сброса пароля (мессенджер):', err);
      alert('Ошибка: ' + err.message);
    }
  };

  // Сброс пароля только для учётной записи Windows (RDP), без изменения пароля в мессенджере
  const handleResetPasswordWindowsForEmployee = async (employee) => {
    const winUser = getWindowsUserForEmployee(employee);
    const winName = String(winUser?.Name || winUser?.name || builtEmployeeWindowsName(employee) || '').trim();
    if (!winName) {
      alert('Не удалось определить имя учётной записи Windows.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Нет токена авторизации');
      return;
    }
    const useCustom = window.confirm('Сбросить пароль только для входа в Windows (RDP).\nOK — ввести свой пароль.\nОтмена — сгенерировать пароль по политике Windows.');
    let newPassword = '';
    if (useCustom) {
      newPassword = window.prompt(
        `Введите новый пароль для учётной записи Windows "${winName}" (мин. 8 символов, заглавные, строчные, цифры, спецсимволы; не должен содержать имя пользователя):`,
        ''
      );
      if (!newPassword) return;
      newPassword = String(newPassword).trim();
      const err = validateWindowsPassword(newPassword, winName);
      if (err) {
        alert(err);
        return;
      }
    } else {
      newPassword = generateWindowsCompliantPassword(12);
    }
    try {
      const winRes = await fetch(`/api/admin/local-users/${encodeURIComponent(winName)}/password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ password: newPassword })
      });
      const winData = await winRes.json().catch(() => ({}));
      if (!winRes.ok || winData.success === false) {
        throw new Error(winData.error || winData.message || 'Ошибка установки пароля в Windows');
      }
      if (!useCustom) {
        window.prompt('Пароль для учётной записи Windows установлен. Передайте сотруднику новый пароль:', newPassword);
      } else {
        alert('Пароль для учётной записи Windows обновлён.');
      }
      loadEmployees();
    } catch (err) {
      console.error('Ошибка сброса пароля (Windows):', err);
      alert('Ошибка: ' + err.message);
    }
  };

  const handleUnlockMessengerLogin = async (employee) => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Нет токена авторизации');
      return;
    }
    const messengerUser = messengerUserForEmployeeMap.get(employee.id);
    if (!messengerUser?.id) {
      alert('Для этого сотрудника не найдена учётка мессенджера.');
      return;
    }
    if (!window.confirm(`Снять временную блокировку входа для "${messengerUser.username || 'пользователя'}"?`)) {
      return;
    }
    try {
      const res = await fetch(`/api/users/${messengerUser.id}/unlock-login`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.error || data.message || 'Ошибка разблокировки входа');
      }
      alert('Временное ограничение входа снято.');
    } catch (err) {
      console.error('Ошибка разблокировки входа:', err);
      alert('Ошибка: ' + (err?.message || err));
    }
  };

  // Отключить или включить учётную запись Windows (одна кнопка: переключает состояние)
  const handleToggleWindowsAccount = async (employee) => {
    const winUser = getWindowsUserForEmployee(employee);
    const winName = String(winUser?.Name || winUser?.name || builtEmployeeWindowsName(employee) || '').trim();
    if (!winName) {
      alert('Не удалось определить имя учётной записи Windows.');
      return;
    }
    const isEnabled = winUser && (winUser.Enabled === true || winUser.Enabled === 'True');
    const action = isEnabled ? 'отключить' : 'включить';
    if (!window.confirm(isEnabled
      ? `Отключить учётную запись Windows "${winName}"? Пользователь не сможет войти по RDP до повторного включения.`
      : `Включить учётную запись Windows "${winName}"?`)) {
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Нет токена авторизации');
      return;
    }
    const endpoint = isEnabled ? 'disable' : 'enable';
    try {
      const res = await fetch(`/api/admin/local-users/${encodeURIComponent(winName)}/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.error || data.message || `Ошибка ${action} учётной записи`);
      }
      alert(`Учётная запись Windows ${isEnabled ? 'отключена' : 'включена'}.`);
      loadLocalWindowsUsers();
    } catch (err) {
      console.error(`Ошибка ${action} учётной записи Windows:`, err);
      alert('Ошибка: ' + err.message);
    }
  };

  const saveWindowsUsername = async (userId, windowsUsername) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`/api/users/${userId}/windows-username`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ windows_username: (windowsUsername || '').trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) throw new Error(data.error || 'Ошибка сохранения');
      setAppUsers(prev => prev.map(u => u.id === userId ? { ...u, windows_username: (windowsUsername || '').trim() || '' } : u));
    } catch (e) {
      alert('Не удалось сохранить Windows-логин: ' + (e.message || e));
    }
  };

  const handleResetPasswordUser = async (user) => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Нет токена авторизации');
      return;
    }
    const useCustom = window.confirm('Нажмите OK, чтобы ввести свой пароль.\nНажмите Отмена, чтобы сгенерировать случайный пароль.');
    let newPassword = '';
    if (useCustom) {
      newPassword = window.prompt(
        `Введите новый пароль для пользователя "${user.username || user.id}" (минимум 6 символов):`,
        ''
      );
      if (!newPassword) return;
      newPassword = String(newPassword).trim();
      if (newPassword.length < 6) {
        alert('Пароль должен содержать минимум 6 символов');
        return;
      }
    } else {
      newPassword = generateWindowsCompliantPassword(12);
    }
    try {
      // 1) Сброс пароля в учётке приложения
      const res = await fetch(`/api/users/${user.id}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ newPassword })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false || data.error) {
        throw new Error(data.error || data.message || 'Ошибка сброса пароля пользователя');
      }

      // 2) Пытаемся обновить пароль локального пользователя Windows с тем же именем
      const localName = (user.username || '').trim();
      if (localName) {
        try {
          const winRes = await fetch(`/api/admin/local-users/${encodeURIComponent(localName)}/password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ password: newPassword })
          });
          const winData = await winRes.json().catch(() => ({}));
          if (!winRes.ok || winData.success === false) {
            console.warn('⚠️ Ошибка обновления пароля локального пользователя Windows:', winData.error || winData.message);
          }
        } catch (winErr) {
          console.warn('⚠️ Ошибка запроса смены пароля локального пользователя Windows:', winErr);
        }
      }

      if (!useCustom) {
        window.prompt(
          `Пароль сброшен.\nПередайте сотруднику новый временный пароль и попросите сменить его после входа.\nНовый пароль:`,
          newPassword
        );
      } else {
        alert('Пароль пользователя успешно обновлён. Все активные сессии будут завершены.');
      }
    } catch (err) {
      console.error('Ошибка сброса пароля пользователя:', err);
      alert('Ошибка сброса пароля: ' + err.message);
    }
  };

  const renderWindowsAccountToggleButton = (employee, winEnabled, winUserForRow, compact = false) => {
    const hasWinAccount = !!winUserForRow;
    if (!hasWinAccount) {
      return (
        <ActionButton
          $compact={compact}
          type="button"
          $type="cancel"
          disabled
          title="Локальная учётка Windows с таким именем не найдена"
        >
          Windows: не найдена
        </ActionButton>
      );
    }
    const enabled = !!winEnabled;
    return (
      <ActionButton
        $compact={compact}
        type="button"
        $type={enabled ? 'win-enabled' : 'win-disabled'}
        onClick={() => handleToggleWindowsAccount(employee)}
        title={
          enabled
            ? 'Учётная запись Windows активна — нажмите, чтобы отключить (RDP будет недоступен)'
            : 'Учётная запись Windows отключена — нажмите, чтобы включить'
        }
      >
        {enabled ? <FiCheck size={compact ? 12 : 14} /> : <FiXCircle size={compact ? 12 : 14} />}
        {compact
          ? enabled
            ? 'Выкл. Windows'
            : 'Вкл. Windows'
          : enabled
            ? 'Отключить учётную запись Windows'
            : 'Включить учётную запись Windows'}
      </ActionButton>
    );
  };

  const renderMobileCompanyEmployeeCard = (employee) => {
    const isEditing = editingId === employee.id;
    const winUserForRow = getWindowsUserForEmployee(employee);
    const winEnabled = winUserForRow && (winUserForRow.Enabled === true || winUserForRow.Enabled === 'True');
    const hasMessengerAccount = !!messengerUserForEmployeeMap.get(employee.id);
    const fullName = `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || '—';
    const listAvatarUrl = getEmployeeListAvatarUrl(employee);

    return (
      <MobileEmployeeCard key={employee.id} $editing={isEditing} $dense={!isEditing}>
        <MobileCardTop $center={!isEditing} $tight={!isEditing}>
          <MobileAvatarButton
            $small={!isEditing}
            type="button"
            aria-label="Загрузить аватар"
            onClick={() => !uploadingAvatar && handleAvatarClick(employee.id)}
          >
            {uploadingAvatar === employee.id ? (
              <MobileAvatarPh $small={!isEditing}>
                <span style={{ fontSize: 9, lineHeight: 1.1, textAlign: 'center' }}>Загрузка…</span>
              </MobileAvatarPh>
            ) : listAvatarUrl ? (
              <>
                <MobileAvatarImg
                  $small={!isEditing}
                  src={normalizeAvatarUrl(listAvatarUrl)}
                  alt={fullName}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) {
                      e.target.nextSibling.style.display = 'flex';
                    }
                  }}
                />
                <MobileAvatarPh $small={!isEditing} style={{ display: 'none' }}>
                  {getInitials(employee.first_name, employee.last_name)}
                </MobileAvatarPh>
              </>
            ) : (
              <MobileAvatarPh $small={!isEditing}>{getInitials(employee.first_name, employee.last_name)}</MobileAvatarPh>
            )}
            <HiddenFileInput
              ref={(el) => { if (el) fileInputRef.current[employee.id] = el; }}
              type="file"
              accept="image/*"
              onChange={(e) => handleAvatarUpload(employee.id, e.target.files?.[0])}
            />
          </MobileAvatarButton>
          <div style={{ flex: 1, minWidth: 0 }}>
            {!isEditing ? (
              <div
                style={{
                  fontWeight: 700,
                  fontSize: '0.88rem',
                  color: '#111827',
                  lineHeight: 1.3,
                  wordBreak: 'break-word'
                }}
              >
                {fullName}
              </div>
            ) : (
              <>
                <MobileFieldBlock>
                  <MobileFieldLabel>Имя</MobileFieldLabel>
                  <EditableInput
                    value={editingData.first_name || ''}
                    onChange={(e) => setEditingData({ ...editingData, first_name: e.target.value })}
                  />
                </MobileFieldBlock>
                <MobileFieldBlock>
                  <MobileFieldLabel>Фамилия</MobileFieldLabel>
                  <EditableInput
                    value={editingData.last_name || ''}
                    onChange={(e) => setEditingData({ ...editingData, last_name: e.target.value })}
                  />
                </MobileFieldBlock>
                <MobileFieldBlock>
                  <MobileFieldLabel>Должность</MobileFieldLabel>
                  <SuggestInput
                    as={EditableInput}
                    listId={`edit-position-mobile-${employee.id}`}
                    value={editingData.position || ''}
                    onChange={(e) => setEditingData({ ...editingData, position: e.target.value })}
                    options={positionOptions}
                    placeholder="Выберите или введите должность"
                  />
                </MobileFieldBlock>
                <MobileFieldBlock>
                  <MobileFieldLabel>Департамент</MobileFieldLabel>
                  <SuggestInput
                    as={EditableInput}
                    listId={`edit-department-mobile-${employee.id}`}
                    value={editingData.department || ''}
                    onChange={(e) => setEditingData({ ...editingData, department: e.target.value })}
                    options={departments}
                    placeholder="Выберите или введите департамент"
                  />
                </MobileFieldBlock>
                <MobileFieldBlock>
                  <MobileFieldLabel>Подразделение</MobileFieldLabel>
                  <EditableInput
                    value={editingData.subdivision || ''}
                    onChange={(e) => setEditingData({ ...editingData, subdivision: e.target.value })}
                  />
                </MobileFieldBlock>
                <MobileFieldBlock>
                  <MobileFieldLabel>Локация</MobileFieldLabel>
                  <EditableInput
                    value={editingData.location || ''}
                    onChange={(e) => setEditingData({ ...editingData, location: e.target.value })}
                  />
                </MobileFieldBlock>
                <MobileFieldBlock>
                  <MobileFieldLabel>Вид занятости</MobileFieldLabel>
                  <EditableSelect
                    value={editingData.employment_type || ''}
                    onChange={(e) => setEditingData({ ...editingData, employment_type: e.target.value })}
                  >
                    <option value="">Выберите вид занятости</option>
                    <option value="Повна зайнятість">Повна зайнятість</option>
                    <option value="Часткова зайнятість">Часткова зайнятість</option>
                    <option value="Контракт">Контракт</option>
                  </EditableSelect>
                </MobileFieldBlock>
                <MobileFieldBlock>
                  <MobileFieldLabel>Дата начала</MobileFieldLabel>
                  <DateInput
                    type="date"
                    value={editingData.start_date || ''}
                    onChange={(e) => setEditingData({ ...editingData, start_date: e.target.value })}
                  />
                </MobileFieldBlock>
                <MobileFieldBlock>
                  <MobileFieldLabel>Исп. срок заканчивается</MobileFieldLabel>
                  <DateInput
                    type="date"
                    value={editingData.end_date || editingData.termination_date || ''}
                    onChange={(e) => setEditingData({
                      ...editingData,
                      end_date: e.target.value,
                      termination_date: e.target.value
                    })}
                  />
                </MobileFieldBlock>
                <MobileFieldBlock>
                  <MobileFieldLabel>Менеджер</MobileFieldLabel>
                  <EditableSelect
                    value={editingData.manager_name || ''}
                    onChange={(e) => setEditingData({ ...editingData, manager_name: e.target.value })}
                  >
                    <option value="">Выберите менеджера</option>
                    {managers.map(mgr => (
                      <option key={mgr} value={mgr}>{mgr}</option>
                    ))}
                  </EditableSelect>
                </MobileFieldBlock>
                <MobileFieldBlock>
                  <MobileFieldLabel>Пароли</MobileFieldLabel>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#6b7280' }}>
                    {(employee.password_messenger || employee.password_windows) ? '••••••••' : '—'}
                  </span>
                </MobileFieldBlock>
              </>
            )}
          </div>
        </MobileCardTop>
        {!isEditing && expandedActionRows.has(employee.id) && (
          <MobileEmployeeDetailsExpand>
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: 6, lineHeight: 1.35 }}>
              {[employee.position, employee.department].filter(Boolean).join(' · ') || '—'}
            </div>
            {employee.subdivision && (
              <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>
                Подразд.: {employee.subdivision}
              </div>
            )}
            {employee.location && (
              <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>
                Локация: {employee.location}
              </div>
            )}
            {employee.employment_type && (
              <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>
                {employee.employment_type}
              </div>
            )}
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>
              Начало: {formatDate(employee.start_date) || '—'} · Оконч.: {formatDate(employee.end_date || employee.termination_date) || '—'}
            </div>
            {employee.manager_name && (
              <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>
                Менеджер: {employee.manager_name}
              </div>
            )}
            <div style={{ fontSize: '0.72rem', fontFamily: 'monospace', color: '#374151', wordBreak: 'break-all' }}>
              <div style={{ marginBottom: 4 }}>
                <span style={{ color: '#6b7280', marginRight: 6 }}>Мессенджер:</span>
                <span title="Можно выделить и скопировать" style={copyablePasswordValueStyle}>
                  {employee.password_messenger || '—'}
                </span>
              </div>
              <div>
                <span style={{ color: '#6b7280', marginRight: 6 }}>Windows:</span>
                <span title="Можно выделить и скопировать" style={copyablePasswordValueStyle}>
                  {employee.password_windows || '—'}
                </span>
              </div>
            </div>
          </MobileEmployeeDetailsExpand>
        )}
        <MobileActionsRow $tight={!isEditing && !expandedActionRows.has(employee.id)}>
          {isEditing ? (
            <>
              <ActionButton $compact type="button" $type="save" onClick={() => handleSave(employee.id)}>
                <FiSave size={12} />
                Сохранить
              </ActionButton>
              <ActionButton $compact type="button" $type="cancel" onClick={handleCancel}>
                Отмена
              </ActionButton>
            </>
          ) : (
            <>
              <ActionButton $compact type="button" $type="edit" onClick={() => handleEdit(employee)}>
                <FiEdit2 size={12} />
                Изменить
              </ActionButton>
              <ActionButton $compact type="button" $type="delete" onClick={() => handleDelete(employee.id, employee)}>
                <FiTrash2 size={12} />
                Удалить
              </ActionButton>
              {!hasMessengerAccount && (
                <ActionButton
                  $compact
                  type="button"
                  $type="save"
                  onClick={() => openCreateMessengerDialog(employee)}
                  title="Создать учётку мессенджера (как при регистрации)"
                >
                  <FiUserPlus size={12} />
                  Учётка (месс.)
                </ActionButton>
              )}
              {expandedActionRows.has(employee.id) ? (
                <>
                  {renderWindowsAccountToggleButton(employee, winEnabled, winUserForRow, true)}
                  <ActionButton
                    $compact
                    type="button"
                    $type="save"
                    onClick={() => handleResetPasswordMessenger(employee)}
                    title="Сбросить пароль только для входа в мессенджер"
                    disabled={!hasMessengerAccount}
                  >
                    Сброс (месс.)
                  </ActionButton>
                  <ActionButton
                    $compact
                    type="button"
                    $type="cancel"
                    onClick={() => handleUnlockMessengerLogin(employee)}
                    title="Снять временную блокировку входа в мессенджер"
                  >
                    Разблок. вход
                  </ActionButton>
                  <ActionButton
                    $compact
                    type="button"
                    $type="save"
                    onClick={() => handleResetPasswordWindowsForEmployee(employee)}
                    title="Сбросить пароль только для учётной записи Windows (RDP)"
                  >
                    Сброс (Win)
                  </ActionButton>
                  <ActionButton
                    $compact
                    type="button"
                    $type="cancel"
                    onClick={() => setExpandedActionRows((prev) => {
                      const next = new Set(prev);
                      next.delete(employee.id);
                      return next;
                    })}
                    title="Свернуть дополнительные действия"
                  >
                    <FiChevronUp size={12} />
                    Свернуть
                  </ActionButton>
                </>
              ) : (
                <ActionButton
                  $compact
                  type="button"
                  $type="cancel"
                  onClick={() => setExpandedActionRows((prev) => new Set(prev).add(employee.id))}
                  title="Показать остальные действия"
                >
                  <FiChevronDown size={12} />
                  Ещё
                </ActionButton>
              )}
            </>
          )}
        </MobileActionsRow>
      </MobileEmployeeCard>
    );
  };

  const renderMobileAppUserCard = (user) => (
    <MobileEmployeeCard key={user.id}>
      <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', lineHeight: 1.25 }}>
        {user.username || '—'}
      </div>
      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 4 }}>
        ID {user.id} · {user.role || 'user'}
      </div>
      <div style={{ fontSize: '0.75rem', color: '#374151', marginTop: 2 }}>
        employee_id: {user.employee_id ?? '—'}
      </div>
      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>
        Департамент: {user.department || '—'}
      </div>
      <MobileFieldBlock style={{ marginTop: 10, marginBottom: 0 }}>
        <MobileFieldLabel>Windows-логин</MobileFieldLabel>
        <MobileCompactInput
          type="text"
          defaultValue={user.windows_username || ''}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v !== (user.windows_username || '')) saveWindowsUsername(user.id, v);
          }}
          placeholder="Как в Windows"
        />
      </MobileFieldBlock>
      <MobileActionsRow>
        <ActionButton
          $compact
          type="button"
          $type="save"
          onClick={() => handleResetPasswordUser(user)}
          title="Сбросить пароль"
        >
          Сброс пароля
        </ActionButton>
      </MobileActionsRow>
    </MobileEmployeeCard>
  );

  const renderMobileWindowsUserCard = (winUser, idx) => {
    const name = winUser.Name || winUser.name || '—';
    const enabled = winUser.Enabled === true || winUser.Enabled === 'True';
    const desc = winUser.Description || winUser.description || '';

    return (
      <MobileEmployeeCard key={`win-local-${idx}-${name}`}>
        <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', lineHeight: 1.25 }}>
          {name}
        </div>
        <div
          style={{
            display: 'inline-block',
            marginTop: 6,
            fontSize: '0.72rem',
            fontWeight: 600,
            padding: '3px 8px',
            borderRadius: 6,
            background: enabled ? 'rgba(67, 233, 123, 0.2)' : 'rgba(148, 163, 184, 0.25)',
            color: enabled ? '#166534' : '#64748b'
          }}
        >
          {enabled ? 'Включён' : 'Отключён'}
        </div>
        {desc && (
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 8, lineHeight: 1.35, wordBreak: 'break-word' }}>
            {desc}
          </div>
        )}
        <MobileActionsRow>
          <ActionButton
            $compact
            type="button"
            $type="save"
            onClick={() => handleResetPasswordWindowsLocal(winUser)}
            title="Сбросить пароль и применить его в Windows и в мессенджере"
          >
            Сброс пароля
          </ActionButton>
        </MobileActionsRow>
      </MobileEmployeeCard>
    );
  };

  const messengerUnlinkedCount = Math.max(0, employees.length - messengerLinkedCount);

  const renderMessengerRegistryMobileCard = (employee) => {
    const mu = messengerUserForEmployeeMap.get(employee.id);
    const fullName = `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || '—';
    const linked = !!mu;
    return (
      <MobileEmployeeCard key={`msg-reg-${employee.id}`}>
        <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', lineHeight: 1.25 }}>
          {fullName}
        </div>
        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 4 }}>
          {[employee.position, employee.department].filter(Boolean).join(' · ') || '—'}
        </div>
        <div
          style={{
            display: 'inline-block',
            marginTop: 8,
            fontSize: '0.72rem',
            fontWeight: 600,
            padding: '4px 10px',
            borderRadius: 8,
            background: linked ? 'rgba(67, 233, 123, 0.2)' : 'rgba(248, 113, 113, 0.15)',
            color: linked ? '#166534' : '#b91c1c'
          }}
        >
          {linked ? `Есть учётка${mu.username ? `: ${mu.username}` : ''}` : 'Нет учётки в мессенджере'}
        </div>
        {linked && (
          <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 6, fontFamily: 'monospace' }}>
            id пользователя: {mu.id}
            {mu.employee_id != null ? ` · employee_id: ${mu.employee_id}` : ''}
          </div>
        )}
        <div style={{ marginTop: 10 }}>
        <MobileActionsRow>
          {!linked && (
            <ActionButton
              $compact
              type="button"
              $type="save"
              onClick={() => openCreateMessengerDialog(employee)}
              title="Создать учётку мессенджера (как при регистрации)"
            >
              <FiUserPlus size={12} />
              Создать учётку
            </ActionButton>
          )}
          <ActionButton
            $compact
            type="button"
            $type="delete"
            onClick={() => deleteMessengerAccountForEmployee(employee)}
            title="Удалить только учётку мессенджера (Windows не трогать)"
          >
            <FiUserX size={12} />
            Удалить учётку
          </ActionButton>
        </MobileActionsRow>
        </div>
      </MobileEmployeeCard>
    );
  };

  const handleOpenUserRightsClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (typeof onOpenUserRights === 'function') {
        onOpenUserRights();
      } else {
        queueMicrotask(() => {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent(OPEN_USER_RIGHTS_MODAL_EVENT));
          }
        });
      }
    },
    [onOpenUserRights]
  );

  if (!open) return null;

  return ReactDOM.createPortal(
    (
      <>
      <ModalOverlay ref={overlayRef} id="company-employees-list-modal-overlay" data-overlay-id="company-employees-list" onClick={onClose}>
        <ModalContainer ref={containerRef} onClick={(e) => e.stopPropagation()}>
          <ModalHeader $compact={isMobile}>
            <ModalTitle $compact={isMobile}>
              <FiUsers />
              {isMobile ? 'Сотрудники компании' : 'Список сотрудников компании'}
            </ModalTitle>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? 6 : 10,
                flexShrink: 0,
              }}
            >
              {canSeeManagementUserRights(state.user?.role) && (
                <button
                  type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={handleOpenUserRightsClick}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    minHeight: isMobile ? 40 : 36,
                    padding: isMobile ? '8px 12px' : '8px 14px',
                    borderRadius: 10,
                    border: '1px solid rgba(196,181,253,0.85)',
                    background: 'linear-gradient(135deg, rgba(139,92,246,0.95) 0%, rgba(91,33,182,0.95) 100%)',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: isMobile ? '0.78rem' : '0.88rem',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 10px rgba(91,33,182,0.45)',
                  }}
                  title="Права пользователей — роли и департаменты"
                >
                  <FiSettings size={isMobile ? 18 : 18} />
                  {isMobile ? 'Права' : 'Права пользователей'}
                </button>
              )}
              <div style={{ position: 'relative', width: isMobile ? 44 : 52, flexShrink: 0 }}>
                <ModalCloseButton onClick={onClose} aria-label="Закрыть" />
              </div>
            </div>
          </ModalHeader>
          <TabsRow $compact={isMobile}>
            <TabButton $compact={isMobile} $active={activeTab === 'company'} onClick={() => setActiveTab('company')}>
              {isMobile ? `Компания (${employees.length})` : `Сотрудники компании (${employees.length})`}
            </TabButton>
            <TabButton $compact={isMobile} $active={activeTab === 'messenger-registry'} onClick={() => setActiveTab('messenger-registry')}>
              {isMobile
                ? `В месс. (${messengerLinkedCount}/${employees.length})`
                : `В мессенджере (${messengerLinkedCount}/${employees.length})`}
            </TabButton>
            <TabButton $compact={isMobile} $active={activeTab === 'in-structure'} onClick={() => setActiveTab('in-structure')}>
              {isMobile ? `Мессенджер (${appUsersInStructure.length})` : `Созданы в мессенджере (${appUsersInStructure.length})`}
            </TabButton>
            <TabButton $compact={isMobile} $active={activeTab === 'windows-local'} onClick={() => setActiveTab('windows-local')}>
              {isMobile ? `Windows (${localWindowsUsers.length})` : `Локальные пользователи Windows (${localWindowsUsers.length})`}
            </TabButton>
          </TabsRow>
          <ModalBody id="company-employees-modal-body">
          <ScrollArea>
          {activeTab === 'company' && (
            <TabPanel>
          <Toolbar $compact={isMobile}>
            <SearchInput
              $compact={isMobile}
              type="text"
              placeholder={isMobile ? 'Поиск…' : 'Поиск по имени, должности, департаменту...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <AddButton $compact={isMobile} onClick={() => setShowAddForm(!showAddForm)}>
              <FiPlus size={isMobile ? 16 : 18} />
              {showAddForm ? 'Отмена' : (isMobile ? 'Добавить' : 'Добавить сотрудника')}
            </AddButton>
          </Toolbar>
          
          <TableScrollViewport>
          <TableContainer $compact={isMobile}>
            {showAddForm && (
              <AddEmployeeForm $compact={isMobile}>
                <FormTitle $compact={isMobile}>
                  <FiPlus />
                  {isMobile ? 'Новый сотрудник' : 'Добавить нового сотрудника'}
                </FormTitle>
                <FormGrid>
                  <FormRow>
                    <FormLabel>Имя *</FormLabel>
                    <FormInput
                      type="text"
                      value={newEmployee.first_name}
                      onChange={(e) => setNewEmployee({ ...newEmployee, first_name: e.target.value })}
                      placeholder="Введите имя"
                    />
                  </FormRow>
                  <FormRow>
                    <FormLabel>Фамилия *</FormLabel>
                    <FormInput
                      type="text"
                      value={newEmployee.last_name}
                      onChange={(e) => setNewEmployee({ ...newEmployee, last_name: e.target.value })}
                      placeholder="Введите фамилию"
                    />
                  </FormRow>
                  <FormRow>
                    <FormLabel>Должность</FormLabel>
                    <SuggestInput
                      listId="new-employee-position-list"
                      value={newEmployee.position}
                      onChange={(e) => setNewEmployee({ ...newEmployee, position: e.target.value })}
                      options={positionOptions}
                      placeholder="Выберите или введите должность"
                    />
                  </FormRow>
                  <FormRow>
                    <FormLabel>Департамент</FormLabel>
                    <SuggestInput
                      listId="new-employee-department-list"
                      value={newEmployee.department}
                      onChange={(e) => setNewEmployee({ ...newEmployee, department: e.target.value })}
                      options={departments}
                      placeholder="Выберите или введите департамент"
                    />
                  </FormRow>
                  <FormRow>
                    <FormLabel>Подразделение</FormLabel>
                    <FormInput
                      type="text"
                      value={newEmployee.subdivision}
                      onChange={(e) => setNewEmployee({ ...newEmployee, subdivision: e.target.value })}
                      placeholder="Введите подразделение"
                    />
                  </FormRow>
                  <FormRow>
                    <FormLabel>Локация</FormLabel>
                    <FormInput
                      type="text"
                      value={newEmployee.location}
                      onChange={(e) => setNewEmployee({ ...newEmployee, location: e.target.value })}
                      placeholder="Введите локацию"
                    />
                  </FormRow>
                  <FormRow>
                    <FormLabel>Вид занятости</FormLabel>
                    <FormSelect
                      value={newEmployee.employment_type}
                      onChange={(e) => setNewEmployee({ ...newEmployee, employment_type: e.target.value })}
                    >
                      <option value="">Выберите вид занятости</option>
                      <option value="Повна зайнятість">Повна зайнятість</option>
                      <option value="Часткова зайнятість">Часткова зайнятість</option>
                      <option value="Контракт">Контракт</option>
                    </FormSelect>
                  </FormRow>
                  <FormRow>
                    <FormLabel>Дата начала</FormLabel>
                    <FormInput
                      type="date"
                      value={newEmployee.start_date}
                      onChange={(e) => setNewEmployee({ ...newEmployee, start_date: e.target.value })}
                    />
                  </FormRow>
                  <FormRow>
                    <FormLabel>Исп. срок заканчивается</FormLabel>
                    <FormInput
                      type="date"
                      value={newEmployee.end_date || newEmployee.termination_date}
                      onChange={(e) => setNewEmployee({ 
                        ...newEmployee, 
                        end_date: e.target.value,
                        termination_date: e.target.value
                      })}
                    />
                  </FormRow>
                  <FormRow>
                    <FormLabel>Менеджер</FormLabel>
                    <FormSelect
                      value={newEmployee.manager_name}
                      onChange={(e) => setNewEmployee({ ...newEmployee, manager_name: e.target.value })}
                    >
                      <option value="">Выберите менеджера</option>
                      {managers.map(mgr => (
                        <option key={mgr} value={mgr}>{mgr}</option>
                      ))}
                    </FormSelect>
                  </FormRow>
                  <FormRow>
                    <FormLabel>Пароль для входа *</FormLabel>
                    <FormInput
                      type="password"
                      value={newEmployee.password}
                      onChange={(e) => setNewEmployee({ ...newEmployee, password: e.target.value })}
                      placeholder="Введите пароль для авторизации"
                      minLength={6}
                    />
                    <small style={{ 
                      color: '#6b7280', 
                      fontSize: '0.85rem', 
                      marginTop: '4px', 
                      display: 'block' 
                    }}>
                      Минимум 6 символов. Этот пароль будет использоваться для входа в приложение.
                    </small>
                  </FormRow>
                </FormGrid>
                <FormActions>
                  <CancelButton onClick={() => {
                    setShowAddForm(false);
                    setNewEmployee({
                      first_name: '',
                      last_name: '',
                      position: '',
                      department: '',
                      subdivision: '',
                      location: '',
                      employment_type: '',
                      start_date: '',
                      end_date: '',
                      termination_date: '',
                      manager_name: '',
                      password: '',
                      avatar_file: null,
                      avatar_preview: null
                    });
                  }}>
                    Отмена
                  </CancelButton>
                  <SubmitButton onClick={handleAddEmployee} disabled={saving}>
                    <FiSave size={16} />
                    {saving ? 'Сохранение...' : 'Сохранить'}
                  </SubmitButton>
                </FormActions>
              </AddEmployeeForm>
            )}
            
            {loading ? (
              <LoadingState $compact={isMobile}>Загрузка сотрудников...</LoadingState>
            ) : filteredEmployees.length === 0 ? (
              <EmptyState $compact={isMobile}>Сотрудники не найдены</EmptyState>
            ) : isMobile ? (
              <MobileCardsStack $tight>
                {filteredEmployees.map(renderMobileCompanyEmployeeCard)}
              </MobileCardsStack>
            ) : (
              <Table>
                <TableHeader>
                  <tr>
                    <TableHeaderCell>Аватарка</TableHeaderCell>
                    <TableHeaderCell>Имя</TableHeaderCell>
                    <TableHeaderCell>Фамилия</TableHeaderCell>
                    <TableHeaderCell>Должность</TableHeaderCell>
                    <TableHeaderCell>Департамент</TableHeaderCell>
                    <TableHeaderCell>Подразделение</TableHeaderCell>
                    <TableHeaderCell>Локация</TableHeaderCell>
                    <TableHeaderCell>Вид занятости</TableHeaderCell>
                    <TableHeaderCell>Дата начала</TableHeaderCell>
                    <TableHeaderCell>Исп. срок заканчивается</TableHeaderCell>
                    <TableHeaderCell>Менеджер</TableHeaderCell>
                    <TableHeaderCell>Пароль</TableHeaderCell>
                    <TableHeaderCell>Действия</TableHeaderCell>
                  </tr>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.map(employee => {
                    const isEditing = editingId === employee.id;
                    const winUserForRow = getWindowsUserForEmployee(employee);
                    const winEnabled = winUserForRow && (winUserForRow.Enabled === true || winUserForRow.Enabled === 'True');
                    const hasMessengerAccount = !!messengerUserForEmployeeMap.get(employee.id);
                    const listAvatarUrl = getEmployeeListAvatarUrl(employee);

                    return (
                      <TableRow key={employee.id} $editing={isEditing}>
                        <TableCell>
                          <AvatarCell onClick={() => !uploadingAvatar && handleAvatarClick(employee.id)}>
                            {uploadingAvatar === employee.id ? (
                              <AvatarPlaceholder>
                                <span style={{ fontSize: '12px' }}>Загрузка...</span>
                              </AvatarPlaceholder>
                            ) : listAvatarUrl ? (
                              <>
                                <AvatarImage 
                                  src={normalizeAvatarUrl(listAvatarUrl)} 
                                  alt={`${employee.first_name} ${employee.last_name}`}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    if (e.target.nextSibling) {
                                      e.target.nextSibling.style.display = 'flex';
                                    }
                                  }}
                                />
                                <AvatarPlaceholder style={{ display: 'none' }}>
                                  {getInitials(employee.first_name, employee.last_name)}
                                </AvatarPlaceholder>
                              </>
                            ) : (
                              <AvatarPlaceholder>
                                {getInitials(employee.first_name, employee.last_name)}
                              </AvatarPlaceholder>
                            )}
                            <HiddenFileInput
                              ref={el => { if (el) fileInputRef.current[employee.id] = el; }}
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleAvatarUpload(employee.id, e.target.files?.[0])}
                            />
                          </AvatarCell>
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <EditableInput
                              value={editingData.first_name || ''}
                              onChange={(e) => setEditingData({ ...editingData, first_name: e.target.value })}
                            />
                          ) : (
                            employee.first_name || '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <EditableInput
                              value={editingData.last_name || ''}
                              onChange={(e) => setEditingData({ ...editingData, last_name: e.target.value })}
                            />
                          ) : (
                            employee.last_name || '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <SuggestInput
                              as={EditableInput}
                              listId={`edit-position-${employee.id}`}
                              value={editingData.position || ''}
                              onChange={(e) => setEditingData({ ...editingData, position: e.target.value })}
                              options={positionOptions}
                              placeholder="Должность"
                            />
                          ) : (
                            employee.position || '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <SuggestInput
                              as={EditableInput}
                              listId={`edit-department-${employee.id}`}
                              value={editingData.department || ''}
                              onChange={(e) => setEditingData({ ...editingData, department: e.target.value })}
                              options={departments}
                              placeholder="Департамент"
                            />
                          ) : (
                            employee.department || '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <EditableInput
                              value={editingData.subdivision || ''}
                              onChange={(e) => setEditingData({ ...editingData, subdivision: e.target.value })}
                            />
                          ) : (
                            employee.subdivision || '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <EditableInput
                              value={editingData.location || ''}
                              onChange={(e) => setEditingData({ ...editingData, location: e.target.value })}
                            />
                          ) : (
                            employee.location || '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <EditableSelect
                              value={editingData.employment_type || ''}
                              onChange={(e) => setEditingData({ ...editingData, employment_type: e.target.value })}
                            >
                              <option value="">Выберите вид занятости</option>
                              <option value="Повна зайнятість">Повна зайнятість</option>
                              <option value="Часткова зайнятість">Часткова зайнятість</option>
                              <option value="Контракт">Контракт</option>
                            </EditableSelect>
                          ) : (
                            employee.employment_type || '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <DateInput
                              type="date"
                              value={editingData.start_date || ''}
                              onChange={(e) => setEditingData({ ...editingData, start_date: e.target.value })}
                            />
                          ) : (
                            formatDate(employee.start_date) || '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <DateInput
                              type="date"
                              value={editingData.end_date || editingData.termination_date || ''}
                              onChange={(e) => setEditingData({ 
                                ...editingData, 
                                end_date: e.target.value,
                                termination_date: e.target.value
                              })}
                            />
                          ) : (
                            formatDate(employee.end_date || employee.termination_date) || '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <EditableSelect
                              value={editingData.manager_name || ''}
                              onChange={(e) => setEditingData({ ...editingData, manager_name: e.target.value })}
                            >
                              <option value="">Выберите менеджера</option>
                              {managers.map(mgr => (
                                <option key={mgr} value={mgr}>{mgr}</option>
                              ))}
                            </EditableSelect>
                          ) : (
                            employee.manager_name || '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <span style={{ fontFamily: 'monospace', fontSize: '0.9rem', color: '#6b7280' }}>
                              {(employee.password_messenger || employee.password_windows) ? '••••••••' : '—'}
                            </span>
                          ) : (
                            <div style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#111827', wordBreak: 'break-all' }}>
                              <div style={{ marginBottom: 4 }}>
                                <span style={{ color: '#6b7280', marginRight: 6 }}>Мессенджер:</span>
                                <span title="Можно выделить и скопировать" style={copyablePasswordValueStyle}>
                                  {employee.password_messenger || '—'}
                                </span>
                              </div>
                              <div>
                                <span style={{ color: '#6b7280', marginRight: 6 }}>Windows:</span>
                                <span title="Можно выделить и скопировать" style={copyablePasswordValueStyle}>
                                  {employee.password_windows || '—'}
                                </span>
                              </div>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            {isEditing ? (
                              <>
                                <ActionButton $type="save" onClick={() => handleSave(employee.id)}>
                                  <FiSave size={14} />
                                  Сохранить
                                </ActionButton>
                                <ActionButton $type="cancel" onClick={handleCancel}>
                                  Отмена
                                </ActionButton>
                              </>
                            ) : (
                              <>
                                <ActionButton $type="edit" onClick={() => handleEdit(employee)}>
                                  <FiEdit2 size={14} />
                                  Редактировать
                                </ActionButton>
                                <ActionButton $type="delete" onClick={() => handleDelete(employee.id, employee)}>
                                  <FiTrash2 size={14} />
                                  Удалить
                                </ActionButton>
                                {!hasMessengerAccount && (
                                  <ActionButton
                                    $type="save"
                                    onClick={() => openCreateMessengerDialog(employee)}
                                    title="Создать учётку мессенджера (как при регистрации)"
                                  >
                                    <FiUserPlus size={14} />
                                    Создать учётку (мессенджер)
                                  </ActionButton>
                                )}
                                {expandedActionRows.has(employee.id) ? (
                                  <>
                                    {renderWindowsAccountToggleButton(employee, winEnabled, winUserForRow, false)}
                                    <ActionButton
                                      $type="save"
                                      onClick={() => handleResetPasswordMessenger(employee)}
                                      title="Сбросить пароль только для входа в мессенджер"
                                      disabled={!hasMessengerAccount}
                                    >
                                      Сбросить пароль (мессенджер)
                                    </ActionButton>
                                    <ActionButton
                                      $type="cancel"
                                      onClick={() => handleUnlockMessengerLogin(employee)}
                                      title="Снять временную блокировку входа в мессенджер"
                                    >
                                      Разблокировать вход
                                    </ActionButton>
                                    <ActionButton
                                      $type="save"
                                      onClick={() => handleResetPasswordWindowsForEmployee(employee)}
                                      title="Сбросить пароль только для учётной записи Windows (RDP)"
                                    >
                                      Сбросить пароль (Windows)
                                    </ActionButton>
                                    <ActionButton
                                      $type="cancel"
                                      onClick={() => setExpandedActionRows(prev => { const next = new Set(prev); next.delete(employee.id); return next; })}
                                      title="Свернуть дополнительные действия"
                                    >
                                      <FiChevronUp size={14} />
                                      Свернуть
                                    </ActionButton>
                                  </>
                                ) : (
                                  <ActionButton
                                    $type="cancel"
                                    onClick={() => setExpandedActionRows(prev => new Set(prev).add(employee.id))}
                                    title="Показать остальные действия"
                                  >
                                    <FiChevronDown size={14} />
                                    Ещё
                                  </ActionButton>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          </TableScrollViewport>
            </TabPanel>
          )}

          {activeTab === 'messenger-registry' && (
            <TabSection $compact={isMobile}>
              <TabIntro $compact={isMobile}>
                {isMobile
                  ? 'Статус учётки, создание (если нет) и удаление только мессенджера — без изменения Windows.'
                  : 'Статус учётки в мессенджере (связь user_id в структуре, затем совпадение ФИО). Можно создать учётку с полями как при регистрации, или удалить только учётку приложения — локальная учётка Windows не затрагивается.'}
              </TabIntro>
              <Toolbar $compact={isMobile}>
                <SearchInput
                  $compact={isMobile}
                  type="text"
                  placeholder={isMobile ? 'Поиск…' : 'Поиск по имени, должности, департаменту...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <EditableSelect
                  value={messengerRegistryFilter}
                  onChange={(e) => setMessengerRegistryFilter(e.target.value)}
                  style={{
                    maxWidth: isMobile ? '100%' : 280,
                    width: isMobile ? '100%' : 'auto',
                    minWidth: isMobile ? 0 : 200,
                    flexShrink: 0
                  }}
                  aria-label="Фильтр по учётке в мессенджере"
                >
                  <option value="all">Все ({employees.length})</option>
                  <option value="linked">С учёткой ({messengerLinkedCount})</option>
                  <option value="unlinked">Без учётки ({messengerUnlinkedCount})</option>
                </EditableSelect>
              </Toolbar>
              {loading || appUsersLoading ? (
                <LoadingState $compact={isMobile}>Загрузка...</LoadingState>
              ) : employees.length === 0 ? (
                <EmptyState $compact={isMobile}>Нет сотрудников в структуре</EmptyState>
              ) : messengerRegistryRows.length === 0 ? (
                <EmptyState $compact={isMobile}>Никого не найдено (измените поиск или фильтр)</EmptyState>
              ) : isMobile ? (
                <TableScrollViewport>
                <MobileCardsStack>
                  {messengerRegistryRows.map(renderMessengerRegistryMobileCard)}
                </MobileCardsStack>
                </TableScrollViewport>
              ) : (
                <TableScrollViewport>
                <TableContainer $compact={isMobile}>
                  <Table>
                    <TableHeader>
                      <tr>
                        <TableHeaderCell>Сотрудник</TableHeaderCell>
                        <TableHeaderCell>Должность / отдел</TableHeaderCell>
                        <TableHeaderCell>Учётка в мессенджере</TableHeaderCell>
                        <TableHeaderCell>Действия</TableHeaderCell>
                      </tr>
                    </TableHeader>
                    <TableBody>
                      {messengerRegistryRows.map((employee) => {
                        const mu = messengerUserForEmployeeMap.get(employee.id);
                        const fullName = `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || '—';
                        const deptLine = [employee.position, employee.department].filter(Boolean).join(' · ') || '—';
                        return (
                          <TableRow key={employee.id}>
                            <TableCell>{fullName}</TableCell>
                            <TableCell>{deptLine}</TableCell>
                            <TableCell>
                              {mu ? (
                                <div style={{ lineHeight: 1.45 }}>
                                  <span
                                    style={{
                                      display: 'inline-block',
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      padding: '3px 8px',
                                      borderRadius: 6,
                                      background: 'rgba(67, 233, 123, 0.2)',
                                      color: '#166534'
                                    }}
                                  >
                                    Есть
                                  </span>
                                  <div style={{ fontSize: '0.8rem', color: '#374151', marginTop: 6 }}>
                                    Логин: {mu.username || '—'}
                                  </div>
                                  <div style={{ fontSize: '0.72rem', color: '#6b7280', fontFamily: 'monospace' }}>
                                    id: {mu.id}
                                    {mu.employee_id != null ? ` · employee_id: ${mu.employee_id}` : ''}
                                  </div>
                                </div>
                              ) : (
                                <span
                                  style={{
                                    display: 'inline-block',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    padding: '3px 8px',
                                    borderRadius: 6,
                                    background: 'rgba(248, 113, 113, 0.15)',
                                    color: '#b91c1c'
                                  }}
                                >
                                  Нет учётки
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
                                {!mu && (
                                  <ActionButton
                                    $type="save"
                                    onClick={() => openCreateMessengerDialog(employee)}
                                    title="Создать учётку мессенджера"
                                  >
                                    <FiUserPlus size={14} />
                                    Создать учётку
                                  </ActionButton>
                                )}
                                <ActionButton
                                  $type="delete"
                                  onClick={() => deleteMessengerAccountForEmployee(employee)}
                                  title="Удалить учётку мессенджера (Windows не трогать)"
                                >
                                  <FiUserX size={14} />
                                  Удалить учётку
                                </ActionButton>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                </TableScrollViewport>
              )}
            </TabSection>
          )}

          {activeTab === 'in-structure' && (
            <TabSection $compact={isMobile}>
              <TabIntro $compact={isMobile}>
                {isMobile
                  ? 'Учётки из мессенджера (в структуре). Сброс пароля, Windows-логин.'
                  : 'Учётные записи, созданные через мессенджер (есть в структуре компании). Можно сбросить пароль.'}
              </TabIntro>
              {appUsersLoading ? (
                <LoadingState $compact={isMobile}>Загрузка...</LoadingState>
              ) : appUsersInStructure.length === 0 ? (
                <EmptyState $compact={isMobile}>Нет пользователей в структуре</EmptyState>
              ) : isMobile ? (
                <TableScrollViewport>
                <MobileCardsStack>
                  {appUsersInStructure.map(renderMobileAppUserCard)}
                </MobileCardsStack>
                </TableScrollViewport>
              ) : (
                <TableScrollViewport>
                <Table>
                  <TableHeader>
                    <tr>
                      <TableHeaderCell>ID</TableHeaderCell>
                      <TableHeaderCell>Имя</TableHeaderCell>
                      <TableHeaderCell>Роль</TableHeaderCell>
                      <TableHeaderCell>employee_id</TableHeaderCell>
                      <TableHeaderCell>Департамент</TableHeaderCell>
                      <TableHeaderCell>Windows-логин</TableHeaderCell>
                      <TableHeaderCell>Действия</TableHeaderCell>
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {appUsersInStructure.map(user => (
                      <TableRow key={user.id}>
                        <TableCell>{user.id}</TableCell>
                        <TableCell>{user.username || '-'}</TableCell>
                        <TableCell>{user.role || 'user'}</TableCell>
                        <TableCell>{user.employee_id ?? '—'}</TableCell>
                        <TableCell>{user.department || '—'}</TableCell>
                        <TableCell>
                          <input
                            type="text"
                            defaultValue={user.windows_username || ''}
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              if (v !== (user.windows_username || '')) saveWindowsUsername(user.id, v);
                            }}
                            placeholder="Как в Windows"
                            style={{ width: '100%', maxWidth: 180, padding: '4px 8px', borderRadius: 6, border: '1px solid #4a5568' }}
                          />
                        </TableCell>
                        <TableCell>
                          <ActionButton $type="save" onClick={() => handleResetPasswordUser(user)} title="Сбросить пароль">
                            Сбросить пароль
                          </ActionButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </TableScrollViewport>
              )}
            </TabSection>
          )}

          {activeTab === 'windows-local' && (
            <TabSection $compact={isMobile}>
              <TabIntro $compact={isMobile}>
                {isMobile
                  ? 'Локальные пользователи Windows. Сброс пароля синхронизирует Windows (RDP) и мессенджер.'
                  : 'Учётные записи, созданные в Windows (Управление компьютером → Локальные пользователи). Сброс пароля устанавливает один и тот же пароль и в Windows (RDP), и в мессенджере.'}
              </TabIntro>
              {localWindowsUsersLoading ? (
                <LoadingState $compact={isMobile}>Загрузка локальных пользователей Windows...</LoadingState>
              ) : localWindowsUsers.length === 0 ? (
                <EmptyState $compact={isMobile}>
                  Нет данных (доступно только на Windows; возможен доступ только для администратора)
                </EmptyState>
              ) : isMobile ? (
                <TableScrollViewport>
                <MobileCardsStack>
                  {localWindowsUsers.map((winUser, idx) => renderMobileWindowsUserCard(winUser, idx))}
                </MobileCardsStack>
                </TableScrollViewport>
              ) : (
                <TableScrollViewport>
                <Table>
                  <TableHeader>
                    <tr>
                      <TableHeaderCell>Имя (Windows)</TableHeaderCell>
                      <TableHeaderCell>Включён</TableHeaderCell>
                      <TableHeaderCell>Описание</TableHeaderCell>
                      <TableHeaderCell>Действия</TableHeaderCell>
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {localWindowsUsers.map((winUser, idx) => (
                      <TableRow key={winUser.Name || winUser.name || idx}>
                        <TableCell>{winUser.Name || winUser.name || '—'}</TableCell>
                        <TableCell>{winUser.Enabled === true || winUser.Enabled === 'True' ? 'Да' : 'Нет'}</TableCell>
                        <TableCell>{winUser.Description || winUser.description || '—'}</TableCell>
                        <TableCell>
                          <ActionButton
                            $type="save"
                            onClick={() => handleResetPasswordWindowsLocal(winUser)}
                            title="Сбросить пароль и применить его в Windows и в мессенджере"
                          >
                            Сбросить пароль
                          </ActionButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </TableScrollViewport>
              )}
            </TabSection>
          )}

          </ScrollArea>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
      {createMessengerEmployee && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-messenger-dialog-title"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000100,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            boxSizing: 'border-box',
          }}
          onClick={closeCreateMessengerDialog}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: 14,
              maxWidth: 440,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: isMobile ? 16 : 24,
              boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
              color: '#111827',
            }}
          >
            <h3 id="create-messenger-dialog-title" style={{ margin: '0 0 8px 0', fontSize: isMobile ? '1.05rem' : '1.2rem', fontWeight: 700 }}>
              Учётка в мессенджере
            </h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '0.85rem', color: '#6b7280', lineHeight: 1.45 }}>
              Поля как при самостоятельной регистрации. Локальные пользователи Windows не создаются и не меняются.
            </p>
            <div style={{ marginBottom: 12, fontSize: '0.95rem', fontWeight: 600 }}>
              {`${createMessengerEmployee.first_name || ''} ${createMessengerEmployee.last_name || ''}`.trim() || '—'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>Пароль</div>
                <EditableInput
                  type="password"
                  autoComplete="new-password"
                  value={createMessengerForm.password}
                  onChange={(e) => setCreateMessengerForm({ ...createMessengerForm, password: e.target.value })}
                  placeholder="Минимум 6 символов"
                />
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 80px', minWidth: 0 }}>
                  <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>День</div>
                  <EditableInput
                    type="number"
                    min={1}
                    max={31}
                    value={createMessengerForm.birth_day}
                    onChange={(e) => setCreateMessengerForm({ ...createMessengerForm, birth_day: e.target.value })}
                    placeholder="1–31"
                  />
                </div>
                <div style={{ flex: '1 1 80px', minWidth: 0 }}>
                  <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>Месяц</div>
                  <EditableInput
                    type="number"
                    min={1}
                    max={12}
                    value={createMessengerForm.birth_month}
                    onChange={(e) => setCreateMessengerForm({ ...createMessengerForm, birth_month: e.target.value })}
                    placeholder="1–12"
                  />
                </div>
                <div style={{ flex: '1 1 100px', minWidth: 0 }}>
                  <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>Год</div>
                  <EditableInput
                    type="number"
                    min={1900}
                    max={2100}
                    value={createMessengerForm.birth_year}
                    onChange={(e) => setCreateMessengerForm({ ...createMessengerForm, birth_year: e.target.value })}
                    placeholder="Год"
                  />
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>Должность (как при регистрации)</div>
                <EditableSelect
                  value={createMessengerForm.department}
                  onChange={(e) => setCreateMessengerForm({ ...createMessengerForm, department: e.target.value })}
                >
                  <option value="">Выберите должность</option>
                  {REGISTRATION_DEPARTMENT_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </EditableSelect>
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <ActionButton $type="cancel" type="button" onClick={closeCreateMessengerDialog} disabled={createMessengerSaving}>
                Отмена
              </ActionButton>
              <ActionButton
                $type="save"
                type="button"
                onClick={submitCreateMessengerAccount}
                disabled={createMessengerSaving}
              >
                {createMessengerSaving ? 'Создание…' : 'Создать учётку'}
              </ActionButton>
            </div>
          </div>
        </div>
      )}
      </>
    ),
    document.body
  );
}

